import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  type SuggestedPhase,
  type SuggestedTask,
  type SuggestedForm,
  type WorkflowSuggestion,
  type WorkflowContextSummary,
} from './workflowDefinitionService';

interface Props {
  suggestion: WorkflowSuggestion;
  loading?: boolean;
  onAccept: (phases: SuggestedPhase[]) => void;
  onCancel: () => void;
}

type CheckState = Record<string, boolean>;

const SOURCE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  curated: { label: 'Curated Blueprint', color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800' },
  ai_generated: { label: 'AI Generated', color: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' },
  hybrid: { label: 'Curated + AI Enriched', color: 'text-lex-accent dark:text-lex-accent', bg: 'bg-lex-accent-soft dark:bg-lex-accent-soft border-lex-accent dark:border-lex-accent' },
};

const CONFIDENCE_STYLES: Record<string, { dot: string; text: string; label: string }> = {
  high: { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', label: 'High confidence' },
  medium: { dot: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', label: 'Medium confidence' },
  low: { dot: 'bg-red-400', text: 'text-red-500 dark:text-red-400', label: 'Low confidence' },
};

const DEADLINE_LABELS: Record<string, { label: string; color: string }> = {
  statutory: { label: 'Statutory deadline', color: 'text-emerald-600 dark:text-emerald-400' },
  common_practice: { label: 'Common practice', color: 'text-amber-600 dark:text-amber-400' },
  estimated: { label: 'Estimated', color: 'text-gray-400' },
};

const WorkflowSuggestionReviewModal: React.FC<Props> = ({ suggestion, loading = false, onAccept, onCancel }) => {
  const initialChecks = useMemo(() => {
    const checks: CheckState = {};
    suggestion.phases.forEach((phase, pi) => {
      checks[`p${pi}`] = true;
      phase.tasks.forEach((_, ti) => {
        checks[`p${pi}t${ti}`] = true;
      });
    });
    return checks;
  }, [suggestion]);

  const [checks, setChecks] = useState<CheckState>(initialChecks);
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(
    () => new Set(suggestion.phases.map((_, i) => i)),
  );
  const [expandedEvidence, setExpandedEvidence] = useState<Set<string>>(new Set());
  const [showContext, setShowContext] = useState(false);

  const toggleCheck = (key: string) => {
    setChecks((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (key.startsWith('p') && !key.includes('t')) {
        const pi = key.slice(1);
        const phase = suggestion.phases[parseInt(pi)];
        phase.tasks.forEach((_, ti) => {
          next[`p${pi}t${ti}`] = next[key];
        });
      }
      return next;
    });
  };

  const togglePhaseExpand = (pi: number) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(pi)) next.delete(pi);
      else next.add(pi);
      return next;
    });
  };

  const toggleEvidence = (key: string) => {
    setExpandedEvidence((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleAccept = () => {
    const acceptedPhases: SuggestedPhase[] = [];
    suggestion.phases.forEach((phase, pi) => {
      if (!checks[`p${pi}`]) return;
      const acceptedTasks: SuggestedTask[] = [];
      phase.tasks.forEach((task, ti) => {
        if (checks[`p${pi}t${ti}`]) {
          acceptedTasks.push(task);
        }
      });
      if (acceptedTasks.length > 0) {
        acceptedPhases.push({ ...phase, tasks: acceptedTasks });
      }
    });
    onAccept(acceptedPhases);
  };

  const totalPhases = suggestion.phases.length;
  const acceptedPhaseCount = suggestion.phases.filter((_, pi) => checks[`p${pi}`]).length;
  const totalTasks = suggestion.phases.reduce((sum, p) => sum + p.tasks.length, 0);
  const acceptedTaskCount = suggestion.phases.reduce((sum, phase, pi) =>
    sum + phase.tasks.filter((_, ti) => checks[`p${pi}t${ti}`]).length, 0);
  const totalForms = suggestion.phases.reduce((sum, p) => sum + p.tasks.reduce((s, t) => s + t.forms.length, 0), 0);
  const verifiedForms = suggestion.phases.reduce((sum, p) => sum + p.tasks.reduce((s, t) => s + t.forms.filter((f) => f.in_catalog).length, 0), 0);
  const aiSuggestedForms = totalForms - verifiedForms;

  const sourceStyle = SOURCE_LABELS[suggestion.source] || SOURCE_LABELS.ai_generated;
  const confidencePct = Math.round(suggestion.confidence * 100);
  const ctx = suggestion.context_summary;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ isolation: 'isolate' }}>
      <div
        className="absolute inset-0 bg-black/60 dark:bg-black/85"
        onClick={onCancel}
        style={{ animation: 'lex-overlay-in 150ms ease-out forwards' }}
      />

      <div
        className="relative z-10 w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ animation: 'lex-modal-in 220ms ease-out forwards' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
              <svg className="h-5 w-5 text-violet-600 dark:text-violet-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Workflow Suggestion</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${sourceStyle.bg}`}>
                  <span className={sourceStyle.color}>{sourceStyle.label}</span>
                </span>
                <span className={`text-[10px] font-medium ${
                  confidencePct >= 80 ? 'text-emerald-600 dark:text-emerald-400' : confidencePct >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'
                }`}>
                  {confidencePct}% confidence
                </span>
              </div>
            </div>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-2.5 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
          <span>{acceptedPhaseCount}/{totalPhases} phases</span>
          <span>{acceptedTaskCount}/{totalTasks} tasks</span>
          <span>{totalForms} form{totalForms !== 1 ? 's' : ''}{verifiedForms > 0 && aiSuggestedForms > 0 ? ` (${verifiedForms} verified, ${aiSuggestedForms} AI)` : aiSuggestedForms > 0 ? ' (AI suggested)' : ''}</span>
          {ctx && (
            <button
              onClick={() => setShowContext(!showContext)}
              className="ml-auto inline-flex items-center gap-1 text-violet-600 dark:text-violet-400 hover:underline text-[10px] font-medium"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              </svg>
              {showContext ? 'Hide' : 'Show'} AI Context
            </button>
          )}
          {!ctx && suggestion.source_authority && (
            <span className="ml-auto truncate" title={suggestion.source_authority}>
              Source: {suggestion.source_authority_url ? (
                <a href={suggestion.source_authority_url} target="_blank" rel="noopener noreferrer" className="text-violet-600 dark:text-violet-400 hover:underline">
                  {suggestion.source_authority}
                </a>
              ) : suggestion.source_authority}
            </span>
          )}
        </div>

        {/* Enhancement #3: Collapsible AI Context Panel */}
        {showContext && ctx && (
          <div className="px-6 py-3 bg-violet-50 dark:bg-violet-950/30 border-b border-violet-200 dark:border-violet-800/50 space-y-2">
            <p className="text-[10px] font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wider">What the AI Knew</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11px]">
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${ctx.forms_in_database > 0 ? 'bg-emerald-500' : 'bg-red-400'}`} />
                <span className="text-gray-600 dark:text-gray-300">{ctx.forms_in_database} forms in database</span>
                {ctx.forms_matched > 0 && <span className="text-emerald-600 dark:text-emerald-400">({ctx.forms_matched} matched)</span>}
                {ctx.forms_ai_suggested > 0 && <span className="text-amber-600 dark:text-amber-400">({ctx.forms_ai_suggested} AI suggested)</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${ctx.online_sources_found > 0 ? 'bg-emerald-500' : 'bg-amber-400'}`} />
                <span className="text-gray-600 dark:text-gray-300">{ctx.online_sources_found} online sources searched</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${ctx.curated_blueprint_available ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                <span className="text-gray-600 dark:text-gray-300">
                  {ctx.curated_blueprint_available
                    ? `Reference blueprint (${ctx.curated_blueprint_jurisdiction})`
                    : 'No reference blueprint'}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${ctx.statute_ref ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                <span className="text-gray-600 dark:text-gray-300">
                  {ctx.statute_ref ? `Statute: ${ctx.statute_ref}` : 'No specific statute ref'}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${ctx.user_description_provided ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                <span className="text-gray-600 dark:text-gray-300">
                  {ctx.user_description_provided ? 'User description included' : 'No user description'}
                </span>
              </div>
            </div>
            {ctx.sources_consulted.length > 0 && (
              <div className="mt-1.5 pt-1.5 border-t border-violet-200 dark:border-violet-800/50">
                <p className="text-[10px] font-medium text-violet-600 dark:text-violet-400 mb-1">Sources consulted:</p>
                <div className="space-y-0.5">
                  {ctx.sources_consulted.slice(0, 3).map((src, i) => (
                    <a key={i} href={src.url} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-violet-600 dark:text-violet-400 hover:underline truncate">
                      {i + 1}. {src.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {suggestion.phases.map((phase, pi) => {
            const phaseKey = `p${pi}`;
            const isPhaseChecked = checks[phaseKey];
            const isExpanded = expandedPhases.has(pi);
            const mandatedTasks = phase.tasks.filter((t) => t.is_court_mandated).length;
            const confStyle = CONFIDENCE_STYLES[phase.confidence] || CONFIDENCE_STYLES.medium;

            return (
              <div key={pi} className={`rounded-lg border transition-colors ${
                isPhaseChecked
                  ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  : 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/30 opacity-60'
              }`}>
                {/* Phase header */}
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => togglePhaseExpand(pi)}>
                  <input
                    type="checkbox"
                    checked={isPhaseChecked}
                    onChange={(e) => { e.stopPropagation(); toggleCheck(phaseKey); }}
                    className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  />
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{phase.name}</span>
                      {phase.is_required && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800">
                          Required
                        </span>
                      )}
                      {/* Enhancement #2: Per-phase confidence indicator */}
                      <span className={`inline-flex items-center gap-1 text-[9px] font-medium ${confStyle.text}`} title={phase.confidence_reason || confStyle.label}>
                        <span className={`w-1.5 h-1.5 rounded-full ${confStyle.dot}`} />
                        {phase.confidence}
                      </span>
                      <span className="text-[10px] text-gray-400">{phase.tasks.length} tasks</span>
                      {mandatedTasks > 0 && (
                        <span className="text-[10px] text-amber-500">{mandatedTasks} mandated</span>
                      )}
                    </div>
                    {/* Phase confidence reason (always visible when expanded) */}
                    {phase.confidence_reason && isExpanded && (
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 italic">{phase.confidence_reason}</p>
                    )}
                  </div>
                  {phase.estimated_duration_days > 0 && (
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{phase.estimated_duration_days}d</span>
                  )}
                </div>

                {/* Tasks (expanded) */}
                {isExpanded && (
                  <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2 space-y-1">
                    {phase.tasks.map((task, ti) => {
                      const taskKey = `p${pi}t${ti}`;
                      const isTaskChecked = checks[taskKey];
                      const evidenceKey = `${pi}-${ti}`;
                      const showEvidence = expandedEvidence.has(evidenceKey);
                      const deadlineInfo = task.deadline_basis ? DEADLINE_LABELS[task.deadline_basis] : null;

                      return (
                        <div key={ti} className={`rounded-md px-3 py-2 transition-colors ${
                          isTaskChecked ? 'bg-gray-50 dark:bg-gray-700/30' : 'opacity-40'
                        }`}>
                          <div className="flex items-start gap-2.5">
                            <input
                              type="checkbox"
                              checked={isTaskChecked}
                              onChange={() => toggleCheck(taskKey)}
                              className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-medium text-gray-800 dark:text-gray-200">{task.title}</span>
                                {task.is_court_mandated && (
                                  <span className="inline-flex items-center px-1 py-0 rounded text-[8px] font-bold uppercase bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                                    Court Mandated
                                  </span>
                                )}
                                <span className="text-[9px] text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{task.task_type}</span>
                                {task.statute_citation && (
                                  <span className="text-[9px] text-violet-600 dark:text-violet-400">
                                    {task.statute_url ? (
                                      <a href={task.statute_url} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-0.5">
                                        <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.875-3.375l4.5-4.5a4.5 4.5 0 00-6.364-6.364L8.257 2.879" />
                                        </svg>
                                        {task.statute_citation}
                                      </a>
                                    ) : task.statute_citation}
                                  </span>
                                )}
                                {/* Enhancement #1: "View Source" toggle */}
                                {task.reasoning && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleEvidence(evidenceKey); }}
                                    className="text-[9px] text-violet-500 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 flex items-center gap-0.5"
                                  >
                                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {showEvidence ? 'Hide' : 'View'} source
                                  </button>
                                )}
                              </div>

                              {/* Enhancement #1: Evidence trail (expanded) */}
                              {showEvidence && task.reasoning && (
                                <div className="mt-1.5 px-2.5 py-2 rounded bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800/50">
                                  <p className="text-[10px] text-gray-700 dark:text-gray-300 leading-relaxed">{task.reasoning}</p>
                                  {deadlineInfo && task.due_date_offset_days > 0 && (
                                    <p className={`text-[9px] mt-1 ${deadlineInfo.color}`}>
                                      {task.due_date_offset_days}d offset — {deadlineInfo.label}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Deadline basis indicator (always visible, compact) */}
                              {!showEvidence && deadlineInfo && task.due_date_offset_days > 0 && (
                                <span className={`text-[9px] ${deadlineInfo.color}`}>
                                  {task.due_date_offset_days}d — {deadlineInfo.label}
                                </span>
                              )}

                              {/* Forms */}
                              {task.forms.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {task.forms.map((form, fi) => (
                                    <FormBadge key={fi} form={form} />
                                  ))}
                                </div>
                              )}
                            </div>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap ${
                              task.priority === 'high' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                                : task.priority === 'medium' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                            }`}>
                              {task.priority}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {suggestion.phases.length === 0 && (
            <div className="text-center py-8 text-sm text-gray-400 dark:text-gray-500">
              No phases were generated. Try a different case subtype or practice area.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <span className="text-xs text-gray-400">
            Uncheck items to exclude them from the workflow
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAccept}
              disabled={loading || acceptedTaskCount === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Applying...
                </>
              ) : (
                <>
                  Accept {acceptedTaskCount} Task{acceptedTaskCount !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Form Badge Sub-Component ───

const FormBadge: React.FC<{ form: SuggestedForm }> = ({ form }) => {
  const tooltipText = form.in_catalog
    ? `${form.form_name} — Verified, PDF available`
    : `${form.form_name} — AI suggested, verify with court`;

  const content = (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium border ${
      form.in_catalog
        ? 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
        : 'bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
    }`} title={tooltipText}>
      {form.in_catalog ? (
        <svg className="w-2.5 h-2.5 text-emerald-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ) : (
        <svg className="w-2.5 h-2.5 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
        </svg>
      )}
      {form.form_number}
      {form.is_mandatory && <span className="text-red-400">*</span>}
    </span>
  );

  const linkUrl = form.pdf_url || form.source_url;
  if (linkUrl) {
    return (
      <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
        {content}
      </a>
    );
  }
  return content;
};

export default WorkflowSuggestionReviewModal;
